/**
 * Semente dos cadastros das Configurações — a mesma do protótipo
 * (`configData.jsx` e `ConfigAtendimentoCrm.jsx`), espelhada no banco pela
 * migração `0080`.
 *
 * Não apague nem consolide registro daqui sem autorização: é especificação
 * funcional (ver `docs/DEMO_DATA_CATALOG.md`).
 */

import type { EscopoDaMensagemRapida, ModoDeDistribuicao, PacoteDoCatalogo } from './cadastros';
import type { ModeloSemente } from './documentosDemo';

export interface DepartamentoSemente {
    id: string;
    nome: string;
    descricao: string;
    ativo: boolean;
}

export interface FilaSemente {
    id: string;
    nome: string;
    departamentoId: string;
    cor: string;
    slaMin: number;
    ativa: boolean;
    /** Ausente é `manual`, como a fila que existia antes da 0161. */
    distribuicao?: ModoDeDistribuicao;
    membrosIds: string[];
}

export interface EtiquetaSemente {
    id: string;
    nome: string;
    cor: string;
    departamentoId?: string;
    filaId?: string;
    gatilhos: string[];
    ativa: boolean;
}

export interface MensagemRapidaSemente {
    id: string;
    gatilho: string;
    titulo: string;
    texto: string;
    escopo: EscopoDaMensagemRapida;
    /** Membro que escreveu. Obrigatório só na privada — "só eu" precisa de um eu. */
    autorId?: string;
    usos: number;
    ativa: boolean;
}

export const DEPARTAMENTOS: DepartamentoSemente[] = [
    { id: 'dep-recepcao', nome: 'Recepção', descricao: 'Primeiro contato, agendamento e confirmação', ativo: true },
    { id: 'dep-comercial', nome: 'Comercial', descricao: 'Leads, orçamentos e follow-up', ativo: true },
    { id: 'dep-clinico', nome: 'Clínico', descricao: 'Dúvidas de procedimento e pós-atendimento', ativo: true },
    { id: 'dep-financeiro', nome: 'Financeiro', descricao: 'Cobrança, notas e conciliação', ativo: true },
];

/* "Bruna Lima" no protótipo é a conta da recepção: aqui, o membro
   "Recepção Jardins". */
export const FILAS_DE_ATENDIMENTO: FilaSemente[] = [
    { id: 'fila-1', nome: 'Recepção · Geral', departamentoId: 'dep-recepcao', cor: '#2986FF', slaMin: 5, ativa: true, membrosIds: ['m-recepcao', 'm-ana'] },
    { id: 'fila-2', nome: 'Recepção · Remarcação', departamentoId: 'dep-recepcao', cor: '#0EA5A4', slaMin: 15, ativa: true, membrosIds: ['m-recepcao'] },
    { id: 'fila-3', nome: 'Comercial · Novos leads', departamentoId: 'dep-comercial', cor: '#22C55E', slaMin: 2, ativa: true, membrosIds: ['m-paulo'] },
    { id: 'fila-4', nome: 'Comercial · Propostas', departamentoId: 'dep-comercial', cor: '#B87503', slaMin: 30, ativa: true, membrosIds: ['m-paulo', 'm-ana'] },
    { id: 'fila-5', nome: 'Clínico · Pós-atendimento', departamentoId: 'dep-clinico', cor: '#8B5CF6', slaMin: 60, ativa: true, membrosIds: ['m-ana'] },
    { id: 'fila-6', nome: 'Financeiro · Cobrança', departamentoId: 'dep-financeiro', cor: '#EC7C1B', slaMin: 240, ativa: false, membrosIds: ['m-recepcao'] },
];

/* A união das duas listas do protótipo: VIP, Primeira consulta e Inadimplente
   aparecem nas duas e são a mesma etiqueta. */
export const ETIQUETAS: EtiquetaSemente[] = [
    { id: 'etq-1', nome: 'VIP', cor: '#B87503', departamentoId: 'dep-recepcao', filaId: 'fila-1', gatilhos: [], ativa: true },
    { id: 'etq-2', nome: 'Primeira consulta', cor: '#2986FF', departamentoId: 'dep-comercial', filaId: 'fila-3', gatilhos: ['primeira vez', 'nunca fui'], ativa: true },
    { id: 'etq-3', nome: 'Orçamento pendente', cor: '#22C55E', departamentoId: 'dep-comercial', filaId: 'fila-4', gatilhos: ['quanto custa', 'valor', 'orçamento'], ativa: true },
    { id: 'etq-4', nome: 'Inadimplente', cor: '#E5484D', departamentoId: 'dep-financeiro', filaId: 'fila-6', gatilhos: ['boleto', 'atrasado'], ativa: true },
    { id: 'etq-5', nome: 'Remarcação', cor: '#0EA5A4', departamentoId: 'dep-recepcao', filaId: 'fila-2', gatilhos: ['remarcar', 'trocar horário', 'desmarcar'], ativa: true },
    { id: 'etq-6', nome: 'Indicação', cor: '#22C55E', gatilhos: [], ativa: true },
    { id: 'etq-7', nome: 'Alergia relatada', cor: '#8B5CF6', gatilhos: [], ativa: true },
    { id: 'etq-8', nome: 'Retorno pendente', cor: '#0EA5A4', gatilhos: [], ativa: false },
];

export const MENSAGENS_RAPIDAS: MensagemRapidaSemente[] = [
    {
        id: 'mr-1', gatilho: 'confirma', titulo: 'Confirmação de agendamento', escopo: 'publica', usos: 412, ativa: true,
        texto: 'Olá {paciente}! Confirmando seu atendimento de {data} às {hora} com {profissional}. Responda *SIM* para confirmar.',
    },
    {
        id: 'mr-2', gatilho: 'endereco', titulo: 'Endereço da clínica', escopo: 'publica', usos: 268, ativa: true,
        texto: 'Estamos na *Rua Pedroso Alvarenga, 1245* — Jardim Paulistano. Fica no conjunto 82. Manda um oi quando chegar!',
    },
    {
        id: 'mr-3', gatilho: 'pos-peeling', titulo: 'Cuidados pós-peeling', escopo: 'publica', usos: 154, ativa: true,
        texto: 'Nas próximas 48h: _sem sol_, sem esfoliante e sem ácido. Use o filtro solar a cada 3 horas. Qualquer ardência fora do normal, me chama.',
    },
    {
        id: 'mr-4', gatilho: 'pix', titulo: 'Dados para pagamento', escopo: 'privada', autorId: 'm-ana', usos: 96, ativa: true,
        texto: 'Chave Pix (CNPJ): *41.882.017/0001-64* — Vecchia Estética e Saúde LTDA. Me envia o comprovante por aqui, por favor.',
    },
];

export const PACOTES_DO_CATALOGO: PacoteDoCatalogo[] = [
    { id: 'pct-1', nome: 'Pacote 10 sessões — Limpeza de pele', sessoes: 10, precoCentavos: 224000, ativo: true },
    { id: 'pct-2', nome: 'Protocolo facial completo — 6 sessões', sessoes: 6, precoCentavos: 318000, ativo: true },
    { id: 'pct-3', nome: 'Depilação corpo inteiro — 8 sessões', sessoes: 8, precoCentavos: 296000, ativo: true },
    { id: 'pct-4', nome: 'Pacote noiva — 4 meses', precoCentavos: 540000, ativo: false },
];

/* Rascunhos sem texto: o protótipo só trazia o nome, e texto de atestado não
   se inventa. */
export const MODELOS_DE_ATESTADO: ModeloSemente[] = [
    { id: 'mdd-at-1', nome: 'Atestado de comparecimento', categoria: 'Atestado', assinantes: 'Não requer assinatura', corpo: '', publicado: false, favorito: false, atualizadoEm: '2026-09-01T09:00:00-03:00' },
    { id: 'mdd-at-2', nome: 'Atestado de repouso', categoria: 'Atestado', assinantes: 'Não requer assinatura', corpo: '', publicado: false, favorito: false, atualizadoEm: '2026-09-01T09:00:00-03:00' },
    { id: 'mdd-at-3', nome: 'Prescrição de home care facial', categoria: 'Prescrição', assinantes: 'Não requer assinatura', corpo: '', publicado: false, favorito: false, atualizadoEm: '2026-09-01T09:00:00-03:00' },
    { id: 'mdd-at-4', nome: 'Prescrição pós-procedimento injetável', categoria: 'Prescrição', assinantes: 'Não requer assinatura', corpo: '', publicado: false, favorito: false, atualizadoEm: '2026-09-01T09:00:00-03:00' },
    { id: 'mdd-at-5', nome: 'Orientações pós-peeling', categoria: 'Prescrição', assinantes: 'Não requer assinatura', corpo: '', publicado: false, favorito: false, atualizadoEm: '2026-09-01T09:00:00-03:00' },
];

/* As etiquetas que o protótipo mostrava nas conversas além de Paciente e Lead
   (que saem do cadastro): Marina é primeira consulta, Júlia é VIP. */
export const ETIQUETAS_DAS_CONVERSAS: { conversaId: string; etiquetaId: string; origem: 'manual' | 'gatilho' }[] = [
    { conversaId: 'conv-2', etiquetaId: 'etq-2', origem: 'manual' },
    { conversaId: 'conv-4', etiquetaId: 'etq-1', origem: 'manual' },
];
